import { useCallback, useEffect, useRef } from "react";

type Grid = string[][];
type Direction = "up" | "down" | "left" | "right";

interface UseGridNavigatorProps {
  grid: Grid;
}

const findIdPosition = (id: string, grid: Grid): [number, number] | null => {
  const rowIndex = grid.findIndex((row) => row.includes(id));
  if (rowIndex === -1) return null;

  const colIndex = grid[rowIndex].indexOf(id);

  if (colIndex === -1) return null;

  return [rowIndex, colIndex];
};

const getIdPosition = (
  id: string,
  grid: Grid,
  idPositionCacheRef: React.RefObject<Map<string, [number, number]>>,
): [number, number] | null => {
  if (!idPositionCacheRef.current) {
    return null;
  }

  if (idPositionCacheRef.current.has(id)) {
    return idPositionCacheRef.current.get(id) ?? null;
  }

  const idPosition = findIdPosition(id, grid);

  if (idPosition) {
    idPositionCacheRef.current.set(id, idPosition);
  }

  return idPosition;
};

// Hook which is used to get the next id based on the arrow key pressed
// in a two-dimensional array of ids.
export const useGridNavigator = (props: UseGridNavigatorProps) => {
  const { grid } = props;
  const idPositionCacheRef = useRef(new Map<string, [number, number]>());

  useEffect(() => {
    // Clear the cache when the grid changes as the ids might have moved.
    idPositionCacheRef.current = new Map();
  }, [grid]);

  const move = useCallback(
    (id: string, direction: Direction) => {
      const idPosition = getIdPosition(id, grid, idPositionCacheRef);

      if (!idPosition) {
        return null;
      }

      const [row, col] = idPosition;

      let newRow = row;
      let newCol = col;

      switch (direction) {
        case "up":
          newRow = row - 1;

          if (newRow < 0) {
            return null;
          }

          // Return the id in the same column of the previous row.
          // If the column does not exist, return the last column of the previous row.
          return (
            grid[newRow]?.[col] ||
            grid[newRow]?.[grid[newRow]?.length - 1] ||
            null
          );
        case "down":
          newRow = row + 1;

          if (newRow >= grid.length) {
            return null;
          }
          // Return the id in the same column of the next row.
          // If the column does not exist, return the last column of the next row.
          return (
            grid[newRow]?.[col] ||
            grid[newRow]?.[grid[newRow]?.length - 1] ||
            null
          );
        case "left":
          newCol = col - 1;

          // Return the id in the same row of the previous column.
          if (newCol >= 0) {
            return grid[row][newCol];
          }

          // Wrap to last column of the previous row
          newRow = row - 1;
          if (newRow >= 0) {
            return grid[newRow]?.[grid[newRow]?.length - 1] || null;
          }

          return null;
        case "right":
          newCol = col + 1;

          // Return the id in the same row of the next column.
          if (newCol < grid[row].length) {
            return grid[row][newCol];
          }

          // Wrap to first column of the next row
          newRow = row + 1;
          if (newRow < grid.length) {
            return grid[newRow]?.[0] || null;
          }
          return null;
        default:
          return null;
      }
    },
    [grid],
  );

  const moveUp = useCallback((id: string) => move(id, "up"), [move]);
  const moveDown = useCallback((id: string) => move(id, "down"), [move]);
  const moveLeft = useCallback((id: string) => move(id, "left"), [move]);
  const moveRight = useCallback((id: string) => move(id, "right"), [move]);

  return {
    moveUp,
    moveDown,
    moveLeft,
    moveRight,
  };
};
